import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { FiMenu, FiX, FiPhoneCall } from "react-icons/fi";
import { SAURABH, GAURI, SITE } from "../utils/siteData";
import OmSymbol from "./OmSymbol";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/pooja-services", label: "Pooja Services" },
  { to: "/astrology-palmistry-services", label: "Astrology & Palmistry" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50">
      {/* Top contact strip */}
      <div className="bg-maroon-900 text-gold-100/90 border-b border-gold-700/30">
        <div className="max-w-7xl mx-auto px-4 h-9 sm:h-10 flex items-center justify-between gap-2 text-xs sm:text-sm">
          <span className="hidden md:flex items-center gap-2 truncate">
            <span className="text-gold-400"><OmSymbol size={16} /></span>
            <span className="text-gold-100/80">Authentic Vedic Poojas &amp; Trusted Astrology Guidance in Thane</span>
          </span>
          <div className="flex items-center gap-1.5 sm:gap-3 mx-auto md:mx-0">
            <a
              href={`tel:+91${SAURABH.phone}`}
              className="flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-white/5 hover:text-gold-300 transition-colors"
            >
              <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-gold-400/70">Saurabh</span>
              <FiPhoneCall className="text-saffron-400" size={13} />
              <span>{SAURABH.phoneDisplay}</span>
            </a>
            <span className="h-3 w-px bg-gold-700/50" aria-hidden />
            <a
              href={`tel:+91${GAURI.phone}`}
              className="flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-white/5 hover:text-gold-300 transition-colors"
            >
              <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-gold-400/70">Gauri</span>
              <FiPhoneCall className="text-saffron-400" size={13} />
              <span>{GAURI.phoneDisplay}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`bg-white/90 backdrop-blur-md transition-shadow duration-300 ${
          scrolled ? "shadow-premium" : "shadow-sm"
        }`}
        style={scrolled ? { borderBottom: "1px solid rgba(237,197,90,0.25)" } : undefined}
      >
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative w-12 h-12 rounded-full bg-maroon-gradient flex items-center justify-center text-gold-200 text-2xl font-heading shadow-gold ring-2 ring-gold-300/40">
              <span className="group-hover:animate-spin-slow inline-block">ॐ</span>
            </div>
            <div>
              <p className="font-heading text-maroon-800 text-lg sm:text-xl leading-tight">
                {SITE.name}
              </p>
              <p className="text-[11px] sm:text-xs text-saffron-700 tracking-wide">
                Saurabh Shastri &amp; Gauri Shastri
              </p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `relative text-sm font-medium transition-colors py-1 ${
                    isActive ? "text-maroon-700" : "text-gray-700 hover:text-saffron-600"
                  } group`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={`absolute left-0 -bottom-0.5 h-[2px] rounded-full bg-gradient-to-r from-saffron-500 to-gold-400 transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to="/marketplace"
              className="px-5 py-2.5 rounded-full text-sm font-semibold border border-maroon-700 text-maroon-700 hover:bg-maroon-50 transition-colors"
            >
              Market
            </Link>
            <Link
              to="/contact"
              className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold"
            >
              Book Now
            </Link>
          </nav>

          <button
            className="lg:hidden text-2xl text-maroon-800 p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <FiX /> : <FiMenu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-gold-200/60 bg-white/95 backdrop-blur-md px-4 pb-4 animate-fade-up">
            <nav className="flex flex-col gap-1 mt-3">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-base font-medium py-2.5 px-3 rounded-lg transition-colors ${
                      isActive
                        ? "text-maroon-700 bg-saffron-50"
                        : "text-gray-700 hover:bg-gold-50 hover:text-saffron-700"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/marketplace"
                onClick={() => setOpen(false)}
                className="px-5 py-3 rounded-full text-center font-semibold border border-maroon-700 text-maroon-700"
              >
                Market
              </Link>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="btn-gold mt-2 px-5 py-3 rounded-full text-center font-semibold"
              >
                Book Now
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
